import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';

import { IosArrowLeft, IosArrowRight } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import {
  Autoplay,
  Navigation,
  Pagination,
  Virtual,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { PARALLEL_IMPORTERS } from '@/constants/global';
import {
  joinSgcmMedia,
  joinSgcmMsSite,
  joinSgcmSite,
} from '@/utils/url/join/public';

import { CAROUSEL_ICON_SIZE } from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const PromotionCarousel: Component = ({ items }) => {
  const tacticalAds = items.filter(item => item.promoType === 'tactical_ads');
  const normalPromos = items.filter(item => item.promoType === 'normal_promo');
  const groupedNormalPromos = [];

  for (let i = 0; i < normalPromos.length; i += 2) {
    groupedNormalPromos.push(normalPromos.slice(i, i + 2));
  }

  const desktopPromoItems = [...tacticalAds, ...groupedNormalPromos];

  return (
    <div className={styles.containerRoot}>
      <div className={styles.containerPromotionBtn}>
        <div className={styles.promotions}>
          <div className={styles.textPromotions}>Promotions</div>
          <Link
            className={styles.btnViewAll}
            href={`${joinSgcmSite('/new_cars/newcars_promotionslist.php')}`}>
            View All
          </Link>
        </div>
      </div>
      <div className={styles.containerDesktopLargeSwiper}>
        <Swiper
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          modules={[Autoplay, Navigation, Virtual]}
          navigation={desktopPromoItems.length > 1
            ? {
              prevEl: '.custom-prev-btn',
              nextEl: '.custom-next-btn',
            }
            : undefined}
          pagination={{
            clickable: true,
          }}
          spaceBetween={30}
          centeredSlides
          virtual>
          {desktopPromoItems.map((promo, index) => {
            // If it's a normalPromo and part of a group (two per group)
            if (Array.isArray(promo)) {
              return (
                <SwiperSlide
                  key={`group-${promo[0].id}`}
                  className={cx(
                    styles.containerDesktopSwiperSlide,
                    styles.containerDesktopSwiperSlideOverride,
                  )}
                  virtualIndex={index}>
                  {promo.map(item => (
                    <Link
                      key={item.id}
                      className={styles.containerNormalPromo}
                      href={`${joinSgcmMsSite(`new-cars/info/${item.carId}/${item.slug}/promo#${item.id}`)}`}>
                      <div>
                        <div className={styles.textDealerName}>
                          {item.dealer.name}
                        </div>
                        <div className={styles.containerContent}>
                          { item.models.length > 1
                            ? (
                              <span className={styles.textMake}>
                                {item.title}
                                {' '}
                                { item.dealer.type === PARALLEL_IMPORTERS
                                && '(PI)' }
                              </span>
                            )
                            : item.models.map(model => (
                              <span
                                key={model.title}
                                className={styles.textMake}>
                                {model.title}
                                {' '}
                                { item.dealer.type === PARALLEL_IMPORTERS
                                && '(PI)' }
                              </span>
                            )) }
                          {' '}
                          -
                          {' '}
                          <span className={styles.textSummary}>
                            {item.summary}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </SwiperSlide>
              );
            }
            // If it's a single promotional item (e.g., tactical_ads)
            return (
              <SwiperSlide
                key={promo.id}
                className={cx(
                  styles.containerDesktopSwiperSlide,
                  styles.containerDesktopSwiperSlideOverride,
                )}>
                <Link
                  className={styles.containerTacticalAds}
                  href={`${joinSgcmMsSite(`new-cars/info/${promo.carId}/${promo.slug}/promo#${promo.id}`)}`}>
                  <div className={styles.containerImage}>
                    <Image
                      alt={promo.description}
                      className={styles.image}
                      height={60}
                      layout="intrinsic"
                      src={`${promo.isPremiumBanner
                        ? `https://i.i-sgcm.com/new_cars/promos/${promo.dealer.id}/${promo.id}_customized_l.jpg`
                        : joinSgcmMedia(`/public/new-cars/promos/${promo.dealer.id}/tactical-ads-${promo.id}-d-728x90.jpg`)}`}
                      width={468} />
                  </div>
                  <div
                    key={promo.id}
                    className={styles.containerAdsDetail}>
                    <div>
                      <div className={styles.textDealerName}>
                        {promo.dealer.name}
                      </div>
                      <div className={styles.containerContent}>
                        { promo.models.length > 1
                          ? (
                            <span className={styles.textMake}>
                              {promo.title}
                              {' '}
                              { promo.dealer.type === PARALLEL_IMPORTERS
                              && '(PI)' }
                            </span>
                          )
                          : promo.models.map(model => (
                            <span
                              key={model.title}
                              className={styles.textMake}>
                              {model.title}
                              {' '}
                              { promo.dealer.type === PARALLEL_IMPORTERS
                              && '(PI)' }
                            </span>
                          )) }
                        {' '}
                        -
                        {' '}
                        <span className={styles.textSummary}>
                          {promo.summary}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            );
          })}
        </Swiper>
        {desktopPromoItems.length > 1 && (
          <>
            <div
              className="custom-prev-btn"
              style={{
                position: 'absolute', left: '10px', top: '19px', zIndex: '200',
              }}>
              <div className={styles.containerArrow}>
                <IosArrowLeft
                  className={styles.arrow}
                  size={CAROUSEL_ICON_SIZE} />
              </div>
            </div>
            <div
              className="custom-next-btn"
              style={{
                position: 'absolute', right: '10px', top: '19px', zIndex: '200',
              }}>
              <div className={styles.containerArrow}>
                <IosArrowRight
                  className={styles.arrow}
                  size={CAROUSEL_ICON_SIZE} />
              </div>
            </div>
          </>
        )}
      </div>
      <div className={styles.containerIpadSwiper}>
        <Swiper
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          modules={[Autoplay, Navigation, Virtual]}
          navigation={items.length > 1
            ? {
              prevEl: '.custom-prev-btn',
              nextEl: '.custom-next-btn',
            }
            : undefined}
          pagination={{
            clickable: true,
          }}
          spaceBetween={30}
          centeredSlides
          virtual>
          {items.map((item, index) => (
            <SwiperSlide
              key={item.id}
              className={styles.containerIpadSwiperSlider}
              virtualIndex={index}>
              <Link
                key={item.id}
                className={styles.containerIpadAdsDetail}
                href={`${joinSgcmMsSite(`new-cars/info/${item.carId}/${item.slug}/promo#${item.id}`)}`}>
                <div>
                  <div className={styles.textDealerName}>
                    {item.dealer.name}
                  </div>
                  <div className={styles.containerContent}>
                    { item.models.length > 1
                      ? (
                        <span className={styles.textMake}>
                          {item.title}
                          {' '}
                          { item.dealer.type === PARALLEL_IMPORTERS
                          && '(PI)' }
                        </span>
                      )
                      : item.models.map(model => (
                        <span
                          key={model.title}
                          className={styles.textMake}>
                          {model.title}
                          {' '}
                          { item.dealer.type === PARALLEL_IMPORTERS
                          && '(PI)' }
                        </span>
                      )) }
                    {' '}
                    -
                    {' '}
                    <span className={styles.textSummary}>
                      {item.summary}
                    </span>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
        {items.length > 1 && (
          <>
            <div
              className="custom-prev-btn"
              style={{
                position: 'absolute', left: '10px', top: '19px', zIndex: '200',
              }}>
              <div className={styles.containerArrow}>
                <IosArrowLeft
                  className={styles.arrow}
                  size={CAROUSEL_ICON_SIZE} />
              </div>
            </div>
            <div
              className="custom-next-btn"
              style={{
                position: 'absolute', right: '10px', top: '19px', zIndex: '200',
              }}>
              <div className={styles.containerArrow}>
                <IosArrowRight
                  className={styles.arrow}
                  size={CAROUSEL_ICON_SIZE} />
              </div>
            </div>
          </>
        )}
      </div>
      <div className={styles.containerMobileSwiper}>
        <Swiper
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          className="mySwiper"
          modules={[Autoplay, Pagination, Navigation, Virtual]}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          loop
          virtual>
          {items?.map((item, index) => (
            <SwiperSlide
              key={item.id}
              className={styles.containerMobileSwiperSlider}
              virtualIndex={index}>
              <Link
                key={item.id}
                className={styles.containerMobilePromo}
                href={`${joinSgcmMsSite(`new-cars/info/${item.carId}/${item.slug}/promo#${item.id}`)}`}>
                <div>
                  <div className={styles.textDealerName}>
                    {item.dealer.name}
                  </div>
                  <div className={styles.containerContent}>
                    { item.models.length > 1
                      ? (
                        <span className={styles.textMake}>
                          {item.title}
                          {' '}
                          { item.dealer.type === PARALLEL_IMPORTERS
                          && '(PI)' }
                        </span>
                      )
                      : item.models.map(model => (
                        <span
                          key={model.title}
                          className={styles.textMake}>
                          {model.title}
                          {' '}
                          { item.dealer.type === PARALLEL_IMPORTERS
                          && '(PI)' }
                        </span>
                      )) }
                    {' '}
                    -
                    {' '}
                    <span className={styles.textSummary}>
                      {item.summary}
                    </span>
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};
